import { Loader2, Send, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { useEffect, useRef, useState } from "react";
import { useStreamChatResponse } from "./useStreamChatResponse";
import ChatBubble from "./ChatBubble";
import { truncateStringToMaxWords } from "../../utils/helpers";
import axiosInstance from "../../services/axiosInstance";
import { useUiStore } from "@/stores/UiStore";

const instance = axiosInstance();
const purposeTextMapping = {
  chat: {
    welcomeMessage:
      "Hi, I'm your Opportunity Chat Assistant! Ask me anything about this contract, and I'll help you explore the details.",
    exploreButton: "Explore this Opportunity",
  },
  proposal: {
    welcomeMessage:
      "Hi, I'm your AI Copilot for Proposals. Select text to edit, ask questions, summarize, or expand effortlessly.",
    exploreButton: "Refine the Proposal",
  },
};

instance.defaults.baseURL += "/chat";

export default function Chat({
  proposal_id,
  purpose,
  searchType,
  context,
  chatData,
  isChatLoading,
  hideCloseButton = false,
  className,
  ...props
}) {
  const {
    mutate: streamChatResponse,
    streamingData,
    streamingDone,
    isLoading,
  } = useStreamChatResponse(proposal_id, purpose, searchType);

  const { welcomeMessage, exploreButton } =
    purposeTextMapping[purpose] || purposeTextMapping.chat;

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: welcomeMessage,
    },
  ]);
  const [chatStatus, setChatStatus] = useState(null);
  const [input, setInput] = useState("");
  const chatContainerRef = useRef(null);

  const inputLength = input.trim().length;
  // Update messages when chatData changes
  useEffect(() => {
    if (chatData?.chat_history && chatData.chat_history.length > 0) {
      setMessages(chatData.chat_history);
    }
    // Always attempt to scroll after chatData loads, even if no messages changed
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }, 100); // Small timeout to ensure DOM has updated
  }, [chatData]);

  // Handle streaming updates
  useEffect(() => {
    if (isLoading && !streamingDone && streamingData) {
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        if (updatedMessages.length > 0) {
          updatedMessages[updatedMessages.length - 1].content = streamingData;
        }
        return updatedMessages;
      });
    }

    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [streamingData, streamingDone, isLoading]);

  useEffect(() => {
    if (isChatLoading) {
      const intervalId = setInterval(async () => {
        // Replace 'yourEndpoint' with your actual endpoint URL
        const response = await instance.get(`/status/${proposal_id}`);
        if (response?.data) {
          setChatStatus(response.data);
        }
      }, 2000); // Check status every second

      return () => clearInterval(intervalId); // Cleanup on component unmount
    }
  }, [isChatLoading, proposal_id, chatStatus, setChatStatus]);

  return (
    <Card {...props} className={cn("h-screen flex flex-col ", className)}>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <CardHeader className=" top-0 flex flex-row items-center pt-2 pb-0">
            <div className="flex items-center space-x-4">
              <Avatar>
                <AvatarImage
                  src="/icons8-quill-with-ink-96.png"
                  alt="Image"
                  className="scale-75"
                />
                <AvatarFallback>AI</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium leading-none">SamSearch AI</p>
                <p className="text-sm text-muted-foreground">{exploreButton}</p>
              </div>
            </div>
            {!hideCloseButton && (
              <>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => useUiStore.setState({ wideLayout: false })}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent sideOffset={10}>Close</TooltipContent>
              </>
            )}
          </CardHeader>
          {isChatLoading && (
            <div className="px-6">
              <p className="text-sm text-muted-foreground mb-1">
                Analyzing opportunity
              </p>
              <Progress
                value={
                  chatStatus && chatStatus?.total_file_count
                    ? (chatStatus?.uploaded_file_ids?.length /
                        chatStatus.total_file_count) *
                      100
                    : 0
                }
                className="mb-4 h-2 animate-pulse"
              />
            </div>
          )}
          <CardContent className={`flex-1 overflow-hidden py-2`}>
            <div
              className="h-full rounded-md border p-4 overflow-y-scroll"
              ref={chatContainerRef}
            >
              <div className="space-y-4">
                {messages?.map((message, index) => (
                  <ChatBubble key={index} message={message} searchType={searchType} proposalId={proposal_id} />
                ))}
              </div>
            </div>
          </CardContent>

          <CardFooter className="pb-2">
            <div className="flex flex-col w-full gap-2">
              {context?.length > 0 && (
                <div className="bg-muted rounded-md p-2 text-muted-foreground text-sm font-extralight">
                  <span className="font-normal">Context: </span>{" "}
                  {truncateStringToMaxWords(context, 13)}
                </div>
              )}
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (inputLength === 0) return;
                  const newMessages = [
                    ...messages,
                    {
                      role: "user",
                      content: input,
                    },
                    {
                      role: "assistant",
                      content: "Thinking...",
                    },
                  ];
                  setMessages(newMessages);
                  streamChatResponse({
                    proposal_id,
                    question: input,
                    context,
                  });
                  setInput("");
                }}
                className="flex flex-row w-full space-x-2"
              >
                <Input
                  id="message"
                  placeholder="Type your message..."
                  className="flex-1"
                  autoComplete="off"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={isChatLoading || !chatData || inputLength === 0}
                >
                  {isChatLoading ? (
                    <Loader2 className="animate-spin h-5 w-5" />
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span className="sr-only">Send</span>
                    </>
                  )}
                </Button>
              </form>
            </div>
          </CardFooter>
        </Tooltip>
      </TooltipProvider>
    </Card>
  );
}
